import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MatrixRain } from "@/components/MatrixRain";
import { Logo } from "@/components/Logo";
import { ArrowRight } from "lucide-react";

// Deep futuristic hum + digital startup blips via Web Audio (best-effort, luxurious & subtle)
function playBootAudio() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    const ctx = new AC();
    const master = ctx.createGain();
    master.gain.value = 0.0001;
    master.connect(ctx.destination);

    const lp = ctx.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = 420;
    lp.connect(master);

    // deep hum (two detuned low oscillators)
    [55, 110, 82.4].forEach((f, i) => {
      const o = ctx.createOscillator();
      o.type = "sine";
      o.frequency.value = f;
      const g = ctx.createGain();
      g.gain.value = i === 0 ? 0.5 : 0.22;
      o.connect(g); g.connect(lp);
      o.start();
    });

    const now = ctx.currentTime;
    master.gain.setValueAtTime(0.0001, now);
    master.gain.exponentialRampToValueAtTime(0.28, now + 0.8);
    master.gain.exponentialRampToValueAtTime(0.14, now + 3.2);
    master.gain.exponentialRampToValueAtTime(0.0001, now + 5.2);

    // digital startup blips
    const blip = (t, freq) => {
      const o = ctx.createOscillator();
      o.type = "triangle";
      o.frequency.value = freq;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, now + t);
      g.gain.exponentialRampToValueAtTime(0.12, now + t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, now + t + 0.18);
      o.connect(g); g.connect(ctx.destination);
      o.start(now + t); o.stop(now + t + 0.2);
    };
    [ [0.6, 880], [0.9, 1320], [1.5, 660], [2.2, 1760], [3.0, 990] ].forEach(([t, f]) => blip(t, f));

    ctx.resume && ctx.resume();
    return ctx;
  } catch (e) { return null; }
}

const STEPS = [
  { at: 2200, text: "MEMORY SYSTEM ONLINE" },
  { at: 3000, text: "IDENTITY MATRIX INITIALIZED" },
];

export const BootSequence = ({ onDone }) => {
  const [showLines, setShowLines] = useState(false);
  const [showRain, setShowRain] = useState(false);
  const [showLogo, setShowLogo] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [showEnter, setShowEnter] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    audioRef.current = playBootAudio();
    const timers = [];
    timers.push(setTimeout(() => setShowLines(true), 250));
    timers.push(setTimeout(() => setShowRain(true), 750));
    timers.push(setTimeout(() => setShowLogo(true), 1400));
    STEPS.forEach((s) => timers.push(setTimeout(() => setMsgs((m) => [...m, s.text]), s.at)));
    timers.push(setTimeout(() => setShowEnter(true), 3700));
    const resume = () => audioRef.current?.resume?.();
    window.addEventListener("pointerdown", resume, { once: true });
    return () => { timers.forEach(clearTimeout); window.removeEventListener("pointerdown", resume); };
  }, []);

  const finish = () => {
    try { audioRef.current && audioRef.current.close(); } catch (e) { console.debug("audio close skipped:", e); }
    onDone && onDone();
  };

  const lines = [
    { cls: "left-0 right-0 top-1/2 h-px", axis: "scaleX", delay: 0 },
    { cls: "top-0 bottom-0 left-1/2 w-px", axis: "scaleY", delay: 0.15 },
    { cls: "left-0 right-0 top-[22%] h-px", axis: "scaleX", delay: 0.3 },
    { cls: "left-0 right-0 bottom-[22%] h-px", axis: "scaleX", delay: 0.45 },
  ];

  return (
    <motion.div
      data-testid="boot-sequence"
      onClick={finish}
      className="fixed inset-0 z-[100] bg-[#050505] flex items-center justify-center overflow-hidden cursor-pointer"
      exit={{ opacity: 0 }} transition={{ duration: 0.6 }}
    >
      {/* neon lines forming */}
      {showLines && lines.map((l, i) => (
        <motion.div key={i} className={`absolute ${l.cls}`}
          style={{ background: "linear-gradient(90deg, transparent, #00f0ff, transparent)", boxShadow: "0 0 12px rgba(0,240,255,0.7)", transformOrigin: "center" }}
          initial={{ [l.axis]: 0, opacity: 0 }} animate={{ [l.axis]: 1, opacity: [0, 1, 0.35] }}
          transition={{ duration: 1.1, delay: l.delay, ease: "easeOut" }} />
      ))}

      <AnimatePresence>
        {showRain && (
          <motion.div key="rain" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1 }} className="absolute inset-0">
            <MatrixRain opacity={0.35} />
          </motion.div>
        )}
      </AnimatePresence>
      <div className="absolute inset-0 cyber-grid opacity-30" />

      <div className="relative z-10 flex flex-col items-center gap-7 px-6 text-center">
        {showLogo && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="animate-flicker"><Logo variant="neon" className="h-24 sm:h-32" /></div>
          </motion.div>
        )}

        <div className="font-mono-tech text-xs sm:text-sm uppercase tracking-[0.3em] space-y-2 min-h-[60px]">
          {msgs.map((m, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
              className="text-neon-turquoise text-glow-turq flex items-center justify-center gap-2">
              <span className="w-1.5 h-1.5 bg-neon-green rounded-full" /> {m}
            </motion.div>
          ))}
        </div>

        {showEnter && (
          <motion.button initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }}
            onClick={finish} data-testid="boot-enter-btn"
            className="mt-2 group inline-flex items-center gap-3 font-mono-tech text-sm uppercase tracking-[0.35em] px-8 py-4 border border-neon-turquoise text-neon-turquoise"
            style={{ boxShadow: "0 0 22px rgba(0,240,255,0.35)" }}>
            Enter FUTRA <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </motion.button>
        )}
      </div>

      <div className="absolute bottom-8 font-mono-tech text-[9px] text-white/25 uppercase tracking-[0.3em]">Click anywhere to skip</div>
    </motion.div>
  );
};
